// Constantes para Clientelo CRM

/**
 * PAISES_CLIENTELO: Single source of truth for all country lists in Clientelo.
 * Ordered by primary market priority (CO first, then rest of LATAM, then US).
 * Used by: Registration, Empresa creation/edit, Store locations, Config→Sistema.
 */
export const PAISES_CLIENTELO = [
  { codigo: 'CO', nombre: 'Colombia', bandera: '🇨🇴', codigoTel: '57', moneda: 'COP', iva: 0.19, zonaHoraria: 'America/Bogota', ciudadDefault: 'Bogotá' },
  { codigo: 'MX', nombre: 'México', bandera: '🇲🇽', codigoTel: '52', moneda: 'MXN', iva: 0.16, zonaHoraria: 'America/Mexico_City', ciudadDefault: 'Ciudad de México' },
  { codigo: 'AR', nombre: 'Argentina', bandera: '🇦🇷', codigoTel: '54', moneda: 'ARS', iva: 0.21, zonaHoraria: 'America/Argentina/Buenos_Aires', ciudadDefault: 'Buenos Aires' },
  { codigo: 'VE', nombre: 'Venezuela', bandera: '🇻🇪', codigoTel: '58', moneda: 'USD', iva: 0.16, zonaHoraria: 'America/Caracas', ciudadDefault: 'Caracas' },
  { codigo: 'HN', nombre: 'Honduras', bandera: '🇭🇳', codigoTel: '504', moneda: 'HNL', iva: 0.15, zonaHoraria: 'America/Tegucigalpa', ciudadDefault: 'Tegucigalpa' },
  { codigo: 'CL', nombre: 'Chile', bandera: '🇨🇱', codigoTel: '56', moneda: 'CLP', iva: 0.19, zonaHoraria: 'America/Santiago', ciudadDefault: 'Santiago' },
  { codigo: 'PE', nombre: 'Perú', bandera: '🇵🇪', codigoTel: '51', moneda: 'PEN', iva: 0.18, zonaHoraria: 'America/Lima', ciudadDefault: 'Lima' },
  { codigo: 'EC', nombre: 'Ecuador', bandera: '🇪🇨', codigoTel: '593', moneda: 'USD', iva: 0.12, zonaHoraria: 'America/Guayaquil', ciudadDefault: 'Quito' },
  { codigo: 'UY', nombre: 'Uruguay', bandera: '🇺🇾', codigoTel: '598', moneda: 'UYU', iva: 0.22, zonaHoraria: 'America/Montevideo', ciudadDefault: 'Montevideo' },
  { codigo: 'PY', nombre: 'Paraguay', bandera: '🇵🇾', codigoTel: '595', moneda: 'PYG', iva: 0.10, zonaHoraria: 'America/Asuncion', ciudadDefault: 'Asunción' },
  { codigo: 'BO', nombre: 'Bolivia', bandera: '🇧🇴', codigoTel: '591', moneda: 'BOB', iva: 0.13, zonaHoraria: 'America/La_Paz', ciudadDefault: 'La Paz' },
  { codigo: 'US', nombre: 'Estados Unidos', bandera: '🇺🇸', codigoTel: '1', moneda: 'USD', iva: 0, zonaHoraria: 'America/New_York', ciudadDefault: '' },
] as const;

export type PaisClientelo = typeof PAISES_CLIENTELO[number];
export type CodigoPais = PaisClientelo['codigo'];

/** Helper: get country config by ISO code */
export function getPaisConfig(codigo: string): PaisClientelo | undefined {
  return PAISES_CLIENTELO.find(p => p.codigo === codigo.toUpperCase());
}

/** Helper: get default city for a country (empty string if not found) */
export function getCiudadDefaultPorPais(codigo: string): string {
  return getPaisConfig(codigo)?.ciudadDefault ?? '';
}

/** Helper: get timezone for a country */
export function getZonaHorariaPorPais(codigo: string): string {
  return getPaisConfig(codigo)?.zonaHoraria ?? 'America/Bogota';
}

/** Helper: get currency for a country */
export function getMonedaPorPais(codigo: string): string {
  return getPaisConfig(codigo)?.moneda ?? 'COP';
}

/** Currencies the merchant can pick for this country (country default first, plus USD / VE extras). */
export function getMonedasSugeridasPorPais(codigo: string): string[] {
  const iso = (codigo || 'CO').toUpperCase();
  if (iso === 'VE') return ['USD', 'VES', 'COP'];
  const def = getMonedaPorPais(iso);
  return def === 'USD' ? ['USD'] : [def, 'USD'];
}

export const MONEDA_NOMBRES: Record<string, string> = {
  COP: 'Peso colombiano',
  USD: 'Dólar americano',
  MXN: 'Peso mexicano',
  ARS: 'Peso argentino',
  VES: 'Bolívar',
  HNL: 'Lempira',
  CLP: 'Peso chileno',
  PEN: 'Sol peruano',
  UYU: 'Peso uruguayo',
  PYG: 'Guaraní',
  BOB: 'Boliviano',
};

/** Common IVA percentages the merchant can apply per product or as company default. 0 = no IVA. */
export const IVA_PORCENTAJES_COMUNES = [0, 5, 8, 10, 12, 16, 18, 19, 21] as const;

/** Helper: get IVA rate for a country */
export function getIvaPorPais(codigo: string): number {
  return getPaisConfig(codigo)?.iva ?? 0.19;
}

/** Helper: get dial code for a country */
export function getCodigoTelPorPais(codigo: string): string {
  return getPaisConfig(codigo)?.codigoTel ?? '57';
}

// Legacy alias for backwards compatibility
export const PAISES_LATAM = PAISES_CLIENTELO;

// Utility functions for IVA calculations
export const IVA_UTILS = {
  // Extract IVA from IVA-inclusive price (Colombia: 19%)
  extractIVA: (priceWithIVA: number, ivaRate: number = 0.19): { basePrice: number; ivaAmount: number } => {
    const basePrice = priceWithIVA / (1 + ivaRate);
    const ivaAmount = priceWithIVA - basePrice;
    return { basePrice, ivaAmount };
  },

  // Add IVA to base price (for IVA-exclusive pricing)
  addIVA: (basePrice: number, ivaRate: number = 0.19): { priceWithIVA: number; ivaAmount: number } => {
    const ivaAmount = basePrice * ivaRate;
    const priceWithIVA = basePrice + ivaAmount;
    return { priceWithIVA, ivaAmount };
  },

  // Get IVA rate for a specific country
  getIVARate: (countryCode: string): number => {
    return getIvaPorPais(countryCode);
  }
};

export const METODOS_PAGO = {
  CO: [
    { codigo: 'EFECTIVO', nombre: 'Efectivo' },
    { codigo: 'TARJETA', nombre: 'Tarjeta de Crédito/Débito' },
    { codigo: 'TRANSFERENCIA', nombre: 'Transferencia Bancaria' },
    { codigo: 'PSE', nombre: 'PSE' },
    { codigo: 'NEQUI', nombre: 'Nequi' },
    { codigo: 'DAVIPLATA', nombre: 'Daviplata' }
  ],
  MX: [
    { codigo: 'EFECTIVO', nombre: 'Efectivo' },
    { codigo: 'TARJETA', nombre: 'Tarjeta de Crédito/Débito' },
    { codigo: 'TRANSFERENCIA', nombre: 'Transferencia Bancaria' },
    { codigo: 'SPEI', nombre: 'SPEI' },
    { codigo: 'OXXO_PAY', nombre: 'Oxxo Pay' },
    { codigo: 'MERCADO_PAGO', nombre: 'Mercado Pago' }
  ],
  AR: [
    { codigo: 'EFECTIVO', nombre: 'Efectivo' },
    { codigo: 'TARJETA', nombre: 'Tarjeta de Crédito/Débito' },
    { codigo: 'TRANSFERENCIA', nombre: 'Transferencia Bancaria' },
    { codigo: 'MERCADO_PAGO', nombre: 'Mercado Pago' },
    { codigo: 'RAPIPAGO', nombre: 'Rapipago' },
    { codigo: 'PAGO_FACIL', nombre: 'Pago Fácil' }
  ],
  PE: [
    { codigo: 'EFECTIVO', nombre: 'Efectivo' },
    { codigo: 'TARJETA', nombre: 'Tarjeta de Crédito/Débito' },
    { codigo: 'TRANSFERENCIA', nombre: 'Transferencia Bancaria' },
    { codigo: 'YAPE', nombre: 'Yape' },
    { codigo: 'PLIN', nombre: 'Plin' },
    { codigo: 'BCP', nombre: 'BCP' }
  ],
  CL: [
    { codigo: 'EFECTIVO', nombre: 'Efectivo' },
    { codigo: 'TARJETA', nombre: 'Tarjeta de Crédito/Débito' },
    { codigo: 'TRANSFERENCIA', nombre: 'Transferencia Bancaria' },
    { codigo: 'TRANSBANK', nombre: 'Transbank' },
    { codigo: 'KHIPU', nombre: 'Khipu' }
  ],
  VE: [
    { codigo: 'EFECTIVO', nombre: 'Efectivo' },
    { codigo: 'TRANSFERENCIA', nombre: 'Transferencia' },
    { codigo: 'ZELLE', nombre: '💸 Zelle' },
    { codigo: 'PAYPAL', nombre: '🌐 PayPal' },
    { codigo: 'USD', nombre: 'Dólares (USD)' },
    { codigo: 'VES', nombre: 'Bolívares (VES)' },
    { codigo: 'COP', nombre: 'Pesos colombianos (COP)' },
    { codigo: 'TARJETA', nombre: 'Tarjeta' },
    { codigo: 'OTRO', nombre: 'Otro' },
  ],
  US: [
    { codigo: 'EFECTIVO', nombre: 'Efectivo' },
    { codigo: 'ZELLE', nombre: '💸 Zelle' },
    { codigo: 'PAYPAL', nombre: '🌐 PayPal' },
    { codigo: 'TARJETA', nombre: 'Tarjeta' },
    { codigo: 'TRANSFERENCIA', nombre: 'Transferencia' },
    { codigo: 'OTRO', nombre: 'Otro' },
  ],
};

/** Monedas comunes en Venezuela (cierre diario por moneda). */
export const MONEDAS_VENEZUELA = ['USD', 'VES', 'COP'] as const;


export const UNIDADES_MEDIDA = [
  'Unidad',
  'Kilogramo',
  'Gramo',
  'Litro',
  'Mililitro',
  'Metro',
  'Centímetro',
  'Metro Cuadrado',
  'Metro Cúbico',
  'Par',
  'Docena',
  'Caja',
  'Paquete',
  'Rollos',
  'Otros'
];

export const ESTADOS_PRODUCTO = [
  { codigo: 'ACTIVO', nombre: 'Activo', color: 'success' },
  { codigo: 'DESCONTINUADO', nombre: 'Descontinuado', color: 'warning' },
  { codigo: 'AGOTADO', nombre: 'Agotado', color: 'danger' }
];

export const ESTADOS_VENTA = [
  { codigo: 'PENDIENTE', nombre: 'Pendiente', color: 'warning' },
  { codigo: 'PAGADA', nombre: 'Pagada', color: 'success' },
  { codigo: 'ANULADA', nombre: 'Anulada', color: 'danger' },
  { codigo: 'DEVUELTA', nombre: 'Devuelta', color: 'info' }
];

export const SEGMENTOS_CLIENTE = [
  { codigo: 'VIP', nombre: 'VIP', color: 'success', descripcion: 'Clientes de alto valor' },
  { codigo: 'REGULAR', nombre: 'Regular', color: 'primary', descripcion: 'Clientes frecuentes' },
  { codigo: 'OCASIONAL', nombre: 'Ocasional', color: 'warning', descripcion: 'Clientes esporádicos' },
  { codigo: 'EN_RIESGO', nombre: 'En Riesgo', color: 'danger', descripcion: 'Clientes en riesgo de churn' },
  { codigo: 'REACTIVADO', nombre: 'Reactivado', color: 'info', descripcion: 'Clientes que volvieron' }
];

export const RIESGOS_CHURN = [
  { codigo: 'BAJO', nombre: 'Bajo', color: 'success', descripcion: 'Riesgo bajo de abandono' },
  { codigo: 'MEDIO', nombre: 'Medio', color: 'warning', descripcion: 'Riesgo medio de abandono' },
  { codigo: 'ALTO', nombre: 'Alto', color: 'danger', descripcion: 'Riesgo alto de abandono' }
];

export const CANALES_COMUNICACION = [
  { codigo: 'WHATSAPP', nombre: 'WhatsApp', icono: '📱' },
  { codigo: 'PRESENCIAL', nombre: 'Presencial', icono: '👥' },
  { codigo: 'TELEFONO', nombre: 'Teléfono', icono: '📞' },
  { codigo: 'EMAIL', nombre: 'Email', icono: '📧' }
];

export const ROLES_USUARIO = [
  { codigo: 'ADMIN', nombre: 'Administrador', descripcion: 'Acceso completo al sistema' },
  { codigo: 'VENDEDOR', nombre: 'Vendedor', descripcion: 'Gestión de ventas y clientes' },
  { codigo: 'CONTADOR', nombre: 'Contador', descripcion: 'Acceso a reportes financieros' }
];

export const PERIODOS_ANALISIS = [
  { codigo: 'HOY', nombre: 'Hoy', dias: 1 },
  { codigo: 'SEMANA', nombre: 'Esta Semana', dias: 7 },
  { codigo: 'MES', nombre: 'Este Mes', dias: 30 },
  { codigo: 'TRIMESTRE', nombre: 'Este Trimestre', dias: 90 },
  { codigo: 'AÑO', nombre: 'Este Año', dias: 365 }
];

export const LIMITES_PAGINACION = [10, 20, 50, 100];

export const TIEMPO_SESION = 24 * 60 * 60 * 1000; // 24 horas en milisegundos

export const TAMANOS_ARCHIVO = {
  MAX_LOGO: 2 * 1024 * 1024, // 2MB
  MAX_IMAGEN_PRODUCTO: 5 * 1024 * 1024, // 5MB
  MAX_DOCUMENTO: 10 * 1024 * 1024 // 10MB
};

export const FORMATOS_ARCHIVO = {
  IMAGENES: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
  DOCUMENTOS: ['.pdf', '.doc', '.docx', '.xls', '.xlsx']
};

export const CONFIGURACION_DEFAULT = {
  itemsPorPagina: 20,
  moneda: 'COP',
  pais: 'CO',
  zonaHoraria: 'America/Bogota',
  idioma: 'es',
  tema: 'light'
};

export const MENSAJES_SISTEMA = {
  ERROR_GENERICO: 'Ha ocurrido un error inesperado. Por favor, inténtelo de nuevo.',
  EXITO_GUARDAR: 'Los datos se han guardado correctamente.',
  EXITO_ELIMINAR: 'El elemento se ha eliminado correctamente.',
  CONFIRMACION_ELIMINAR: '¿Está seguro de que desea eliminar este elemento?',
  SESION_EXPIRADA: 'Su sesión ha expirado. Por favor, inicie sesión nuevamente.',
  PERMISO_DENEGADO: 'No tiene permisos para realizar esta acción.',
  DATOS_REQUERIDOS: 'Por favor, complete todos los campos requeridos.',
  VALIDACION_ERROR: 'Por favor, corrija los errores en el formulario.'
};

export const REGEX_VALIDACION = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  TELEFONO: /^[+]?[0-9\s\-()]{10,}$/,
  SKU: /^[A-Za-z0-9-_]+$/,
  NOMBRE: /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/,
  SOLO_NUMEROS: /^[0-9]+$/,
  SOLO_LETRAS: /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/
};

export const COLORES_TEMA = {
  primario: '#3B82F6',
  secundario: '#6B7280',
  exito: '#10B981',
  advertencia: '#F59E0B',
  peligro: '#EF4444',
  info: '#06B6D4'
};

export const BREAKPOINTS = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536
};



